import React, { useMemo, useState, useRef, useEffect, useCallback } from "react";
import { postDataToTheServer } from "../../../../helpers/api";
import AppURL from "../../../../constants/AppURL";
import { ToastError, ToastSuccess } from "../../../../constants/Toast";

// ─── Types ────────────────────────────────────────────────────────────────────
export interface BillRecord {
    sn: number;
    yearr: number;
    monthh: number;
    consume_unit: number;
    consume_amt: number;
    bill_no: number;
    mem_name: string;
    tole_name: string;
    add_eng: string;
    phone_no?: string;
    member_sn: number;
    date_n?: string;
    date_e?: string;
    unit_total?: number;
    meter_sn?: string;
    post?: number;
    reader?: number;
    meter_status?: number;
    deleted?: number;
}

export interface ApiResponse {
    results?: BillRecord[];
    status?: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────
const MONTHS: { key: number; label: string; short: string }[] = [
    { key: 1, label: "Baisakh", short: "Bai" },
    { key: 2, label: "Jestha", short: "Jes" },
    { key: 3, label: "Ashadh", short: "Ash" },
    { key: 4, label: "Shrawan", short: "Shr" },
    { key: 5, label: "Bhadra", short: "Bha" },
    { key: 6, label: "Aswin", short: "Asw" },
    { key: 7, label: "Kartik", short: "Kar" },
    { key: 8, label: "Mangsir", short: "Man" },
    { key: 9, label: "Poush", short: "Pou" },
    { key: 10, label: "Magh", short: "Mag" },
    { key: 11, label: "Falgun", short: "Fal" },
    { key: 12, label: "Chaitra", short: "Cha" },
];

// ─── Cell / Override Types ────────────────────────────────────────────────────
export interface CellData {
    unit: number;
    amt: number;
    billNo: number;
    sn: number;
    member_sn: number;
    dateN?: string;
    dateE?: string;
    unitTotal?: number;
}

interface EditState {
    year: number;
    month: number;
    cell: CellData;
    anchorRect: DOMRect;
}

// key = "year_month"
type Overrides = Record<string, { unit: number; amt: number }>;

function cellKey(year: number, month: number) {
    return `${year}_${month}`;
}

// ─── buildPivot ───────────────────────────────────────────────────────────────
function buildPivot(records: BillRecord[], overrides: Overrides) {
    const yearSet = new Set<number>();
    records.forEach((r) => yearSet.add(r.yearr));
    const years = Array.from(yearSet).sort((a, b) => a - b);

    const grid: Record<number, Record<number, CellData>> = {};
    years.forEach((y) => (grid[y] = {}));

    records.forEach((r) => {
        const cur = grid[r.yearr]?.[r.monthh];
        if (!cur || r.consume_unit > cur.unit) {
            grid[r.yearr][r.monthh] = {
                unit: r.consume_unit,
                amt: r.consume_amt,
                billNo: r.bill_no,
                member_sn: r.member_sn,
                sn: r.sn,
                dateN: r.date_n,
                dateE: r.date_e,
                unitTotal: r.unit_total,
            };
        }
    });

    // Apply local overrides
    Object.keys(overrides).forEach((key) => {
        const parts = key.split("_");
        const y = Number(parts[0]);
        const m = Number(parts[1]);
        if (grid[y]?.[m]) {
            grid[y][m] = { ...grid[y][m], ...overrides[key] };
        }
    });

    // Row stats
    const rowStats: Record<number, { total: number; count: number; max: number; maxMonth: number }> = {};
    years.forEach((y) => {
        let total = 0, count = 0, max = -Infinity, maxMonth = 0;
        MONTHS.forEach(({ key: m }) => {
            const c = grid[y][m];
            if (c) {
                total += c.unit;
                count++;
                if (c.unit > max) { max = c.unit; maxMonth = m; }
            }
        });
        rowStats[y] = { total, count, max: max < 0 ? 0 : max, maxMonth };
    });

    // Col stats
    const colStats: Record<number, { max: number; maxYear: number }> = {};
    MONTHS.forEach(({ key: m }) => {
        let max = -Infinity, maxYear = 0;
        years.forEach((y) => {
            const c = grid[y][m];
            if (c && c.unit > max) { max = c.unit; maxYear = y; }
        });
        colStats[m] = { max: max < 0 ? 0 : max, maxYear };
    });

    const globalMax = records.reduce((mx, r) => (r.consume_unit > mx ? r.consume_unit : mx), 0);
    const totalAmt = records.reduce((s, r) => s + r.consume_amt, 0);

    return { years, grid, rowStats, colStats, globalMax, totalAmt };
}

interface EditPopupProps {
    editState: EditState;
    onSave: (year: number, month: number, unit: number, amt: number) => void;
    onClose: () => void;
    refetch: any
}

const EditPopup: React.FC<EditPopupProps> = ({ editState, refetch, onSave, onClose }) => {
    const { year, month, cell, anchorRect } = editState;
    const [unit, setUnit] = useState(String(cell.unitTotal));
    const [amt, setAmt] = useState(String(cell.amt));
    const [error, setError] = useState("");
    const popupRef = useRef<HTMLDivElement>(null);
    const unitRef = useRef<HTMLInputElement>(null);

    const monthLabel = MONTHS.find((m) => m.key === month)?.label ?? "";

    const popStyle = useMemo(() => {
        const POP_W = 268, POP_H = 260; // Reduced height (removed unitTotal row)
        const vw = window.innerWidth, vh = window.innerHeight;
        let top = anchorRect.bottom + 6;
        let left = anchorRect.left;
        if (top + POP_H > vh - 8) top = anchorRect.top - POP_H - 6;
        if (top < 8) top = 8;
        if (left + POP_W > vw - 8) left = vw - POP_W - 8;
        if (left < 8) left = 8;
        return { top, left, width: POP_W };
    }, [anchorRect]);

    useEffect(() => { unitRef.current?.focus(); unitRef.current?.select(); }, []);
    useEffect(() => {
        const h = (e: MouseEvent) => {
            if (popupRef.current && !popupRef.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener("mousedown", h);
        return () => document.removeEventListener("mousedown", h);
    }, [onClose]);
    useEffect(() => {
        const h = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", h);
        return () => document.removeEventListener("keydown", h);
    }, [onClose]);

    const handleSave = async () => {
        const u = parseFloat(unit);
        const a = parseFloat(amt);
        if (isNaN(u) || u < 0) { setError("Unit must be a valid number ≥ 0"); return; }
        if (isNaN(a) || a < 0) { setError("Amount must be a valid number ≥ 0"); return; }
        const formData = new FormData();
        formData.append("sn", cell.sn?.toString())
        formData.append("unit_total", u?.toString())
        formData.append("consume_amt", a?.toString())

        try {

            const res: any = await postDataToTheServer(
                AppURL.AccountURL + "account/member/unit/and/amount/update/view/",
                formData
            );

            if (res?.status === 200) {
                ToastSuccess({
                    message: res?.message || "Updated successfully"
                });
                refetch(cell.member_sn);
            } else {
                ToastError({
                    message: res?.response?.data?.error || "Something went wrong"
                });
            }

        } catch (error: any) {

            console.log(error?.response?.data?.error);

            ToastError({
                message:
                    error?.response?.data?.error ||
                    "Server Error"
            });
        }
        onSave(year, month, u, a);
        onClose();
    };

    const onKeyDown = (e: React.KeyboardEvent) => { if (e.key === "Enter") handleSave(); };
    return (
        <div
            ref={popupRef}
            className="fixed z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden"
            style={popStyle}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <div className="bg-blue-600 px-4 py-3 flex items-start justify-between">
                <div>
                    <p className="text-white text-sm font-bold leading-tight">Edit Record</p>
                    <p className="text-blue-200 text-[11px] mt-0.5">
                        {monthLabel} {year}
                        {cell.billNo ? ` · Bill #${cell.billNo}` : ""}
                        {cell.dateN ? ` · ${cell.dateN}` : ""}
                    </p>
                </div>
                <button
                    onClick={onClose}
                    className="text-blue-200 hover:text-white text-xl leading-none ml-3 mt-0.5 transition-colors"
                    title="Close (Esc)"
                >
                    ×
                </button>
            </div>

            <div className="px-4 py-4 space-y-3">
                {/* Unit */}
                <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                        Consume Unit
                    </label>
                    <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
                        <span className="px-3 py-2.5 bg-slate-50 text-slate-400 text-xs font-semibold border-r border-slate-200 select-none">
                            kWh
                        </span>
                        <input
                            ref={unitRef}
                            type="number"
                            min="0"
                            step="0.01"
                            value={unit}
                            onChange={(e) => { setUnit(e.target.value); setError(""); }}
                            onKeyDown={onKeyDown}
                            className="flex-1 px-3 py-2.5 text-sm font-mono text-slate-800 outline-none bg-white"
                            placeholder="0"
                        />
                    </div>
                </div>

                {/* Amount */}
                <div>
                    <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5">
                        Amount
                    </label>
                    <div className="flex items-center rounded-lg border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition-all">
                        <span className="px-3 py-2.5 bg-slate-50 text-slate-400 text-xs font-semibold border-r border-slate-200 select-none">
                            Rs.
                        </span>
                        <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={amt}
                            onChange={(e) => { setAmt(e.target.value); setError(""); }}
                            onKeyDown={onKeyDown}
                            className="flex-1 px-3 py-2.5 text-sm font-mono text-slate-800 outline-none bg-white"
                            placeholder="0"
                        />
                    </div>
                </div>

                {error && (
                    <p className="text-xs text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
                        ⚠ {error}
                    </p>
                )}

                <div className="flex gap-2 pt-0.5">
                    <button
                        onClick={onClose}
                        className="flex-1 py-2.5 text-xs font-semibold rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 active:scale-95 transition-all"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        className="flex-1 py-2.5 text-xs font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 active:scale-95 transition-all shadow-sm"
                    >
                        Save
                    </button>
                </div>
            </div>

            <div className="pb-3 text-center text-[10px] text-slate-300">
                Enter to save &nbsp;·&nbsp; Esc to cancel
            </div>
        </div>
    );
};
const ManageUnitListView: React.FC<{ onMahasulList?: any, refetch: any }> = ({ onMahasulList, refetch }) => {
    const [highlightYear, setHighlightYear] = useState<number | null>(null);
    const [highlightMonth, setHighlightMonth] = useState<number | null>(null);
    const [showAmt, setShowAmt] = useState(false);
    const [editState, setEditState] = useState<EditState | null>(null);
    const [overrides, setOverrides] = useState<Overrides>({});
    const [savedFlash, setSavedFlash] = useState<string | null>(null);

    const records = useMemo<BillRecord[]>(
        () => (Array.isArray(onMahasulList?.results) ? onMahasulList!.results! : []),
        [onMahasulList]
    );

    const { years, grid, rowStats, colStats, globalMax, totalAmt } = useMemo(
        () => buildPivot(records, overrides),
        [records, overrides]
    );

    const member = records[0];
    const activeMonths = MONTHS.filter(({ key }) => years.some((y) => grid[y]?.[key] !== undefined));

    const handleDblClick = useCallback(
        (e: React.MouseEvent<HTMLTableCellElement>, year: number, month: number, cell: CellData) => {
            e.preventDefault();
            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
            console.log(cell)
            setEditState({ year, month, cell, anchorRect: rect });
        },
        []
    );

    const handleSave = useCallback((year: number, month: number, unit: number, amt: number) => {
        const key = cellKey(year, month);
        setOverrides((prev) => ({ ...prev, [key]: { unit, amt } }));
        setSavedFlash(key);
        setTimeout(() => setSavedFlash(null), 1600);
    }, []);

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-800">

            {/* ── Header ─────────────────────────────────────────────────────── */}
            <div className="bg-white border-b border-slate-200 px-4 sm:px-6 py-4">
                <div className="max-w-full mx-auto flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center text-white text-lg font-bold shadow">
                            ⚡
                        </div>
                        <div>
                            <h1 className="text-lg font-bold text-slate-800 leading-tight tracking-tight">
                                Watersupply Consumption Report
                            </h1>
                            {member && (
                                <p className="text-xs text-slate-400 mt-0.5">
                                    {member.mem_name} &nbsp;·&nbsp; {member.tole_name}, {member.add_eng}
                                    {member.meter_sn && <> &nbsp;·&nbsp; Meter: {member.meter_sn}</>}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {Object.keys(overrides).length > 0 && (
                            <span className="text-xs px-2.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 border border-amber-200 font-medium animate-pulse">
                                ✏ {Object.keys(overrides).length} edited
                            </span>
                        )}
                        <button
                            onClick={() => setShowAmt((v) => !v)}
                            className={`text-xs px-3 py-1.5 rounded-lg border font-medium transition-all ${showAmt
                                ? "bg-blue-600 text-white border-blue-600"
                                : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
                                }`}
                        >
                            {showAmt ? "Showing Amount" : "Show Amount"}
                        </button>
                        <span className="text-xs px-2.5 py-1.5 rounded-lg bg-slate-100 text-slate-500 border border-slate-200 font-medium">
                            {records.length} records
                        </span>
                        <span className="text-xs px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200 font-medium">
                            {years.length} year{years.length !== 1 ? "s" : ""}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Stat bar ───────────────────────────────────────────────────── */}
            <div className="bg-white border-b border-slate-100 px-4 sm:px-6 py-2.5">
                <div className="max-w-full mx-auto flex gap-6 overflow-x-auto text-xs text-slate-500 flex-wrap items-center">
                    <span>
                        Peak Unit: <strong className="text-rose-600 font-bold">{globalMax} kWh</strong>
                    </span>
                    <span>
                        Total Billed: <strong className="text-slate-700">Rs. {totalAmt.toLocaleString()}</strong>
                    </span>
                    {years.length > 0 && (
                        <span>
                            Period:{" "}
                            <strong className="text-slate-700">
                                {years.reduce((a, b) => Math.min(a, b), years[0])} –{" "}
                                {years.reduce((a, b) => Math.max(a, b), years[0])} BS
                            </strong>
                        </span>
                    )}
                    <span className="text-slate-300 italic hidden sm:inline">
                        Double-click any cell to edit
                    </span>
                </div>
            </div>

            {/* ── Pivot Table ────────────────────────────────────────────────── */}
            <div className="px-2 sm:px-4 py-3">
                <div className="overflow-x-auto rounded-sm border border-gray-300 bg-white">
                    <table className="border-collapse text-sm" style={{ minWidth: "900px", width: "100%" }}>
                        <thead>
                            <tr>
                                {/* Year column header */}
                                <th className="bg-gray-50 text-gray-800 text-xs font-semibold px-3 py-1.5 text-left border border-gray-300 select-none">
                                    Year
                                </th>
                                {/* Month headers */}
                                {activeMonths.map(({ key, label, short }) => (
                                    <th
                                        key={key}
                                        className={`text-xs font-semibold px-2 py-1.5 text-center border border-gray-300 cursor-pointer select-none transition-colors duration-100 ${highlightMonth === key
                                            ? "bg-amber-100 text-amber-800"
                                            : "bg-gray-50 text-gray-700 hover:bg-gray-100"
                                            }`}
                                        onClick={() => setHighlightMonth((prev) => (prev === key ? null : key))}
                                        title={`Click to highlight ${label}`}
                                    >
                                        <span className="hidden sm:inline">{label}</span>
                                        <span className="sm:hidden">{short}</span>
                                    </th>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {years.length === 0 && (
                                <tr>
                                    <td
                                        colSpan={activeMonths.length + 1}
                                        className="py-16 text-center text-slate-300 text-sm"
                                    >
                                        No data available
                                    </td>
                                </tr>
                            )}

                            {years.map((year, yi) => {
                                const isHlRow = highlightYear === year;
                                return (
                                    <tr
                                        key={year}
                                        className={`transition-colors duration-75 ${yi % 2 === 0 ? "bg-white" : "bg-slate-50/60"
                                            } ${isHlRow ? "bg-amber-50" : ""}`}
                                    >
                                        {/* Year label cell */}
                                        <td
                                            className={`px-3 py-0 border border-gray-300 cursor-pointer select-none transition-colors ${isHlRow
                                                ? "bg-amber-100 text-amber-900"
                                                : "bg-gray-50 text-gray-800 hover:bg-gray-100"
                                                }`}
                                            onClick={() => setHighlightYear((prev) => (prev === year ? null : year))}
                                            title="Click to highlight row"
                                        >
                                            <div className="font-bold text-sm py-1">{year}</div>
                                        </td>

                                        {/* Month data cells */}
                                        {activeMonths.map(({ key: m }) => {
                                            const cell = grid[year]?.[m];
                                            const isColHL = highlightMonth === m;
                                            const isRowHL = highlightYear === year;
                                            const key = cellKey(year, m);
                                            const isEdited = !!overrides[key];
                                            const isFlash = savedFlash === key;

                                            /* ── Empty cell ── */
                                            if (!cell) {
                                                return (
                                                    <td
                                                        key={m}
                                                        className={`border border-gray-300 text-center py-1 px-2 text-gray-300 text-xs transition-colors ${isColHL || isRowHL ? "bg-amber-50" : ""
                                                            }`}
                                                    >
                                                        —
                                                    </td>
                                                );
                                            }

                                            /* ── Data cell ── */
                                            return (
                                                <td
                                                    key={m}
                                                    className={`relative border border-gray-300 text-center py-0 px-0 select-none cursor-pointer group transition-all duration-150 ${isFlash
                                                        ? "bg-emerald-100 ring-2 ring-inset ring-emerald-400"
                                                        : isEdited
                                                            ? "bg-blue-50"
                                                            : isColHL || isRowHL
                                                                ? "bg-amber-50"
                                                                : "hover:bg-blue-50/50"
                                                        }`}
                                                    onDoubleClick={(e) => handleDblClick(e, year, m, cell)}
                                                    title="Double-click to edit"
                                                >
                                                    <div className="py-1 px-2 font-mono text-sm leading-none">
                                                        {showAmt ? (
                                                            <span className="text-xs">
                                                                {cell.amt > 0 ? `₨${cell.amt}` : "—"}
                                                            </span>
                                                        ) : (
                                                            <>
                                                                {cell.unitTotal != null
                                                                    ? cell.unitTotal
                                                                    : <span className="text-slate-300">—</span>
                                                                }
                                                            </>
                                                        )}
                                                    </div>

                                                    {/* Blue dot: locally edited */}
                                                    {isEdited && !isFlash && (
                                                        <span
                                                            className="absolute top-0.5 right-0.5 w-1.5 h-1.5 rounded-full bg-blue-500"
                                                            title="Locally edited"
                                                        />
                                                    )}

                                                    {/* Green check: just saved */}
                                                    {isFlash && (
                                                        <span className="absolute top-0.5 right-0.5 text-emerald-600 text-[10px] font-black">
                                                            ✓
                                                        </span>
                                                    )}

                                                    {/* Hover hint */}
                                                    <span className="absolute bottom-0.5 right-1 text-[9px] text-blue-400 font-medium opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                                                        edit
                                                    </span>
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Legend ─────────────────────────────────────────────────────── */}
            <div className="px-4 sm:px-6 pb-2 flex gap-4 text-xs text-slate-400 flex-wrap">
                <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />
                    Locally edited
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 inline-block" />
                    Just saved
                </span>
                <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-sm bg-amber-100 border border-amber-300 inline-block" />
                    Highlighted row/col
                </span>
            </div>

            {/* ── Edit Popup ─────────────────────────────────────────────────── */}
            {editState && (
                <EditPopup
                    editState={editState}
                    refetch={refetch}
                    onSave={handleSave}
                    onClose={() => setEditState(null)}
                />
            )}

            {/* ── Footer ─────────────────────────────────────────────────────── */}
            <div className="px-4 sm:px-6 py-3 text-xs text-slate-300 text-center border-t border-slate-100 bg-white mt-1">
                Double-click any cell to edit &nbsp;·&nbsp; Click year/month header to highlight &nbsp;·&nbsp; Blue dot = locally edited
            </div>
        </div>
    );
};

export default ManageUnitListView;